import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { AuthService } from './auth.service';
import { AuthChangeEvent, AuthError, Session, SupabaseClient } from '@supabase/supabase-js';
import { User as UserSupabase } from '@supabase/supabase-js';
import { Router } from '@angular/router';
import { AuthErrorData } from '../types/auth.types';
import TYPES from '../constants/types';
import { ConfigService } from './config.service';
import { DATABASE_SERVICE, DatabaseService } from './database.service';
import { User } from '../models/user';
import { SUPABASE } from '../app.config';
import { environment } from '../../environments/environment';
import { PsychologistService } from './psychologist.service';
import { Psychologist } from '../models/psychologists';

@Injectable({
  providedIn: 'root'
})
export class SupabaseAuthService implements AuthService{
  
  user: WritableSignal<User | null> = signal(null);
  
  private PROFILES:any;
  private TABLES:any;

  private supabase: SupabaseClient = inject(SUPABASE);
  private databaseService: DatabaseService<User> = inject(DATABASE_SERVICE);
  private psychologistService: PsychologistService<Psychologist> = inject(PsychologistService<Psychologist>);

  constructor(
    private configService: ConfigService,
    private router: Router,
  ) {
    this.supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, session: Session | null) => {

      console.log('Auth change event:', event);
      
      if (session && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
        const user: User | null = this.mapUser(session.user);
        if(user){      
          if(!user.account_registered){
            user.profile = this.PROFILES['USER'].key;
            user.account_registered = true;
            user.terms_accepted = false;
            user.policies_accepted = false;
            user.prefered_language = this.configService.preferedLanguage();
            
            const userStored = await this.databaseService.getById(this.TABLES.USER, user.id)
            if(!userStored){
              await this.databaseService.create(this.TABLES.USER, user);
            }
            await this.updateUser(user);
          }
          
          this.user.set(user);

          if(user.prefered_language){
            configService.setLanguage(user.prefered_language);
          }
          
          if(user.policies_accepted && user.terms_accepted){
            const pathname = window.location.pathname;
            if(pathname != '/login'){
              this.router.navigateByUrl(pathname, { replaceUrl: true });
            }else{
              this.router.navigateByUrl('/home', { replaceUrl: true });
            }
          }else{
            this.router.navigate(['/complete-account'], { replaceUrl: true }); //TODO: NEEDS TO VERIFY WHEN TOKEN IS BEING REFRESHED
          }
        }else{
          this.router.navigate(['/login'], { replaceUrl: true }); //TODO: MUST BE ERROR PAGE  
        }
      } else if (session && event === 'USER_UPDATED') {
        const user: User | null = this.mapUser(session.user);
        this.user.set(user);
      } else if (event === 'PASSWORD_RECOVERY'){
        this.router.navigate(['/reset-password'], { replaceUrl: true });
      } else if (event === 'SIGNED_OUT') {
        this.router.navigate(['/login'], { replaceUrl: true });
      }
    });

    this.PROFILES = TYPES.DATABASE.PROFILES;
    this.TABLES = TYPES.DATABASE.TABLES;

  }

  async signInWithGoogle(): Promise<AuthErrorData | null> {
    const { error } = await this.supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: environment.HOST+'/login-callback',
      },
    });
    return this.mapError(error);
  }

  async signInWithEmail(email: string, password: string): Promise<AuthErrorData | null> {
    const { error } = await this.supabase.auth.signInWithPassword({ email, password });
    return this.mapError(error);
  }

  async signUpWithEmail(email: string, password: string): Promise<AuthErrorData | null> {
    const { data, error } = await this.supabase.auth.signUp(
      { email, password,
         options: {
          emailRedirectTo: `${window.location.origin}/login-callback`,
          data: {
            profile: this.PROFILES['USER'].key,
            account_registered: false,
            terms_accepted: false,
            policies_accepted: false,
            prefered_language: this.configService.preferedLanguage()
          } 
        }
      }
    );
    return this.mapError(error);
  }

  async signOut() : Promise<AuthErrorData | null> {
    const { error } = await this.supabase.auth.signOut();
    if(!error){
      this.user.set(null);
      this.psychologistService.psychologist.set(null);
    }
    return this.mapError(error);
  }

  async deleteAccount() : Promise<AuthErrorData | null> {
    const session = (await this.supabase.auth.getSession()).data.session;
    if (!session) {
      throw new Error("Usuário não autenticado");
    }
    
    const { error } = await this.supabase.functions.invoke('delete_user_account', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }

    });

    return this.mapError(error);
  }

  async requestPasswordReset(email: string): Promise<AuthErrorData | null> {
    const { error } = await this.supabase.auth.resetPasswordForEmail(email, {
      redirectTo: environment.HOST+'/reset-password'
    });
    return this.mapError(error);
  }

  async resendConfirmationEmail(email:string): Promise<AuthErrorData | null>{
    const { error } = await this.supabase.auth.resend({
      type: 'signup',
      email: email,
    });

    return this.mapError(error);
  }

  async isLoggedIn():Promise<boolean> {
    if (await this.getUser()){
      return true;
    }
    return false;
  }

  async handleLoginCallback(): Promise<AuthErrorData | null> {
    const { error } = await this.supabase.auth.getSession();
    if(!error){
      this.router.navigate(['/home'], { replaceUrl: true });
    }
    return this.mapError(error);
  }

  async getUser(): Promise<User | null> {
    const { data } = await this.supabase.auth.getUser();
    const userMapped = this.mapUser(data.user);
    this.user.set(userMapped);
    return userMapped;
  }

  async updateUser(user:Partial<User>): Promise<AuthErrorData | null>{
    const { data, error } = await this.supabase.auth.updateUser({
      data: {
        ...user
      }
    });

    this.user.set(this.mapUser( data.user));

    return this.mapError(error);
  }

  async updatePassword(password:string): Promise<AuthErrorData | null>{
    const { error } = await this.supabase.auth.updateUser({
      password
    });

    return this.mapError(error);
  }

  //Specify function for Supabase session
  async getSession(){
    return this.supabase.auth.getSession();
  }

  private mapError(error: AuthError | null): AuthErrorData | null {
    if (!error) return null;
    return {
      message: error.message,
      code: error.code,
    };
  }

  private mapUser(userProvided: UserSupabase | null): User | null {
    if (!userProvided) return null;
    return {
      id: userProvided.id,
      email: userProvided.email || null,
      first_name: userProvided.user_metadata['first_name'] || null,
      last_name: userProvided.user_metadata['last_name'] || null,
      phone_number: userProvided.user_metadata['phone_number'] || null,
      birthdate: userProvided.user_metadata['birthdate'] || null,
      profile_img_url: userProvided.user_metadata['profile_img_url'] || null,
      profile: userProvided.user_metadata['profile'] || null,
      account_registered: userProvided.user_metadata['account_registered'] || null,
      terms_accepted: userProvided.user_metadata['terms_accepted'] || null,
      policies_accepted: userProvided.user_metadata['policies_accepted'] || null,
      prefered_language: userProvided.user_metadata['prefered_language'] || null,
      created_at: userProvided.created_at || null,
      updated_at: userProvided.updated_at || null
    };
  }
  
}
